/**
 * 产品健康度视图
 * 层面一：产品健康度与用户参与度
 */

import React from 'react';
import { Users, Activity, Target, TrendingUp } from 'lucide-react';
import MetricCard from '../charts/MetricCard';
import CompositeMetricGauge from '../charts/CompositeMetricGauge';
import TrendChart from '../charts/TrendChart';
// Using any for now to fix TypeScript compilation
type ProductHealthMetrics = any;
type TimeSeriesDataPoint = any;
type TrendAnalysis = any;

interface ProductHealthViewProps {
  productHealth: ProductHealthMetrics;
  userStats: any;
  funnelStats: any;
  timeSeries: {
    dau: TimeSeriesDataPoint[];
    wau: TimeSeriesDataPoint[];
    mau: TimeSeriesDataPoint[];
    coreActivationRate: TimeSeriesDataPoint[];
    practitionerEngagement: TimeSeriesDataPoint[];
  };
  trends: {
    dau: TrendAnalysis;
    coreActivationRate: TrendAnalysis;
    practitionerEngagement: TrendAnalysis;
  };
}

const ProductHealthView: React.FC<ProductHealthViewProps> = ({
  productHealth,
  userStats,
  funnelStats,
  timeSeries,
  trends
}) => {
  // PES组件分解
  const pesComponents = [
    {
      name: '每周会话数',
      value: productHealth.details.weeklySessionsPerUser,
      weight: 0.2,
      color: '#3b82f6' // blue
    },
    {
      name: '每周病历数',
      value: productHealth.details.weeklyRecordsPerUser,
      weight: 0.5,
      color: '#10b981' // green
    },
    {
      name: '每周贡献数',
      value: productHealth.details.weeklyContributionsPerUser,
      weight: 0.3,
      color: '#f59e0b' // amber
    }
  ];

  // 获取核心激活率告警
  const getCoreActivationAlert = () => {
    if (productHealth.coreValueActivationRate < 0.3) {
      return {
        level: 'critical' as const,
        message: '核心价值激活率过低，需要紧急关注用户转化'
      };
    }
    if (productHealth.coreValueActivationRate < 0.5) {
      return {
        level: 'warning' as const,
        message: '核心价值激活率偏低，建议优化用户引导流程'
      };
    }
    return undefined;
  };

  // 获取PES告警
  const getPESAlert = () => {
    if (productHealth.practitionerEngagementScore < 0.5) {
      return {
        level: 'critical' as const,
        message: '从业者参与度极低，需要加强用户激励措施'
      };
    }
    if (productHealth.practitionerEngagementScore < 1.0) {
      return {
        level: 'warning' as const,
        message: '从业者参与度偏低，建议推出用户激励活动'
      };
    }
    return undefined;
  };

  // 获取AI依赖度告警
  const getAiDependencyAlert = () => {
    if (productHealth.aiDependencyRate < 0.3) {
      return {
        level: 'critical' as const,
        message: 'AI功能使用率过低，核心价值未被用户认知'
      };
    }
    if (productHealth.aiDependencyRate < 0.6) {
      return {
        level: 'warning' as const,
        message: 'AI功能使用率偏低，建议优化AI功能体验'
      };
    }
    return undefined;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">产品健康度与用户参与度</h2>
        <p className="text-gray-600 mt-1">
          监控核心价值激活、用户参与和AI功能采用情况
        </p>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="核心价值激活率"
          value={productHealth.coreValueActivationRate}
          format="percentage"
          subtitle={`${productHealth.details.activatedUsers}/${productHealth.details.totalUsers} 用户`}
          trend={{
            direction: trends.coreActivationRate.trend === 'increasing' ? 'up' : 
                      trends.coreActivationRate.trend === 'decreasing' ? 'down' : 'stable',
            value: Math.abs(trends.coreActivationRate.changeRate),
            label: '相比上期'
          }}
          alert={getCoreActivationAlert()}
          icon={Target}
        />

        <MetricCard
          title="从业者参与度指数"
          value={productHealth.practitionerEngagementScore}
          format="custom"
          subtitle="基于会话、病历和贡献"
          trend={{
            direction: trends.practitionerEngagement.trend === 'increasing' ? 'up' : 
                      trends.practitionerEngagement.trend === 'decreasing' ? 'down' : 'stable',
            value: Math.abs(trends.practitionerEngagement.changeRate),
            label: '相比上期'
          }}
          alert={getPESAlert()}
          icon={Activity}
        />

        <MetricCard
          title="AI依赖度"
          value={productHealth.aiDependencyRate}
          format="percentage"
          subtitle={`${productHealth.details.aiAssistedRecords}/${productHealth.details.totalRecords} 病历`}
          alert={getAiDependencyAlert()}
          icon={TrendingUp}
        />

        <MetricCard
          title="日活跃用户"
          value={userStats.dau}
          format="number"
          subtitle="当前活跃度"
          trend={{
            direction: trends.dau.trend === 'increasing' ? 'up' : 
                      trends.dau.trend === 'decreasing' ? 'down' : 'stable',
            value: Math.abs(trends.dau.changeRate),
            label: '相比昨日'
          }}
          icon={Users}
        />
      </div>

      {/* 复合指标仪表盘 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 从业者参与度指数分解 */}
        <CompositeMetricGauge
          title="从业者参与度指数 (PES)"
          value={productHealth.practitionerEngagementScore}
          maxValue={5}
          description="综合评估从业者在平台上的参与程度"
          components={pesComponents}
          thresholds={{
            excellent: 2.5,
            good: 1.5,
            warning: 0.8
          }}
        />

        {/* 用户转化漏斗 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">用户转化漏斗</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                <span className="text-sm text-gray-700">拍照</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{funnelStats.photoTaken.toLocaleString()}</div>
                <div className="text-xs text-gray-500">100.0%</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded mr-3"></div>
                <span className="text-sm text-gray-700">AI请求</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{funnelStats.aiRequestSent.toLocaleString()}</div>
                <div className="text-xs text-gray-500">
                  {(funnelStats.conversionRates.photoToRequest * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-amber-500 rounded mr-3"></div>
                <span className="text-sm text-gray-700">AI响应</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{funnelStats.aiResponseReceived.toLocaleString()}</div>
                <div className="text-xs text-gray-500">
                  {(funnelStats.conversionRates.requestToResponse * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-purple-500 rounded mr-3"></div>
                <span className="text-sm text-gray-700">保存病历</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{funnelStats.noteSaved.toLocaleString()}</div>
                <div className="text-xs text-gray-500">
                  {(funnelStats.conversionRates.responseToSave * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">整体转化率</span>
                <span className="text-lg font-bold text-blue-600">
                  {(funnelStats.conversionRates.overallConversion * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 趋势图表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DAU趋势 */}
        <TrendChart
          title="日活跃用户趋势"
          data={timeSeries.dau}
          chartType="area"
          color="#3b82f6"
          formatValue={(value) => value.toLocaleString()}
          formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric' 
          })}
          trendAnalysis={trends.dau}
          thresholds={{
            warning: Math.max(...timeSeries.dau.map(d => d.value)) * 0.7,
            critical: Math.max(...timeSeries.dau.map(d => d.value)) * 0.5
          }}
        />

        {/* 核心激活率趋势 */}
        <TrendChart
          title="核心价值激活率趋势"
          data={timeSeries.coreActivationRate}
          chartType="line"
          color="#10b981"
          formatValue={(value) => `${(value * 100).toFixed(1)}%`}
          formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric' 
          })}
          trendAnalysis={trends.coreActivationRate}
          thresholds={{
            warning: 0.5,
            critical: 0.3
          }}
        />

        {/* 从业者参与度趋势 */}
        <TrendChart
          title="从业者参与度指数趋势"
          data={timeSeries.practitionerEngagement}
          chartType="line"
          color="#f59e0b"
          formatValue={(value) => value.toFixed(2)}
          formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric' 
          })}
          trendAnalysis={trends.practitionerEngagement}
          thresholds={{
            warning: 1.0,
            critical: 0.5
          }}
        />

        {/* WAU/MAU趋势对比 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">用户活跃度对比</h3>
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{userStats.dau.toLocaleString()}</div>
                <div className="text-sm text-gray-600">日活跃</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{userStats.wau.toLocaleString()}</div>
                <div className="text-sm text-gray-600">周活跃</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{userStats.mau.toLocaleString()}</div>
                <div className="text-sm text-gray-600">月活跃</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center text-sm text-gray-600">
              <div>DAU/WAU: {((userStats.dau / userStats.wau) * 100).toFixed(1)}%</div>
              <div>WAU/MAU: {((userStats.wau / userStats.mau) * 100).toFixed(1)}%</div>
              <div>DAU/MAU: {((userStats.dau / userStats.mau) * 100).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductHealthView;